import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary — captura errores de render en el árbol hijo y muestra
 * un fallback en lugar de una pantalla en blanco.
 *
 * Uso básico:
 *   <ErrorBoundary>
 *     <ComponentQuePuedeFallar />
 *   ErrorBoundary>
 *
 * Con fallback personalizado:
 *   <ErrorBoundary fallback={<p>Algo salió mal</p>}>
 *     ...
 *   </ErrorBoundary>
 *
 * Con etiqueta de sección (aparece en el mensaje de error):
 *   <ErrorBoundary section="Panel de teletrabajo">
 *     ...
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console in development; could be sent to a monitoring service later
    console.error('[ErrorBoundary]', this.props.section || 'unknown section', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const section = this.props.section || 'esta sección';
    const isDev = process.env.NODE_ENV === 'development';

    return (
      <div
        data-testid="error-boundary-fallback"
        className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-destructive/30 bg-destructive/5 p-8 text-center min-h-[160px]"
      >
        <AlertTriangle className="h-8 w-8 text-destructive/70" />
        <div className="space-y-1">
          <p className="font-semibold text-sm text-destructive">
            No se pudo cargar {section}
          </p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Ha ocurrido un error inesperado. Puedes intentar recargarlo o refrescar la página.
          </p>
          {isDev && this.state.error && (
            <p className="text-xs font-mono text-destructive/60 mt-2 max-w-sm break-all">
              {this.state.error.message}
            </p>
          )}
        </div>
        <button
          onClick={this.handleReset}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Reintentar
        </button>
      </div>
    );
  }
}

/**
 * withErrorBoundary — HOC para envolver un componente de función
 * en un ErrorBoundary sin cambiar el JSX padre.
 *
 * const SafeComponent = withErrorBoundary(MyComponent, 'Mi Sección');
 */
export function withErrorBoundary(WrappedComponent, section) {
  function Wrapped(props) {
    return (
      <ErrorBoundary section={section}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  }
  Wrapped.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return Wrapped;
}
